import React from 'react';

import {GetDrawerNavigationBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {useNavigationCategory} from './useNavigationCategory';

import styled from 'styled-components';
import {FirstLevelCategoryTree} from './CategoryTree/FirstLevelCategoryTree';
import {SecondLevelCategoryTree} from './CategoryTree/SecondLevelCategoryTree';
import {useFocusOnFirstLevelClickedButton} from './categoryTreeFocusManagementHelpers';
import {OFFCANVAS_DEFAULT_QUERY_PARAMETER} from './useOffcanvasOpenState';

interface OffcanvasCategoryTreeViewProps {
  isTransitioning: boolean;
  onClick?: () => void;
  onCategoryClick?: VoidFunction;
}

export const OffcanvasCategoryTreeView: React.FC<
  GetDrawerNavigationBulkResponse['categoryTree'] &
    OffcanvasCategoryTreeViewProps
> = ({isTransitioning, onClick, onCategoryClick, treeItems}) => {
  const {currentCategoryId} = useNavigationCategory();

  const onFirstLevelCategoryClick = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
  ) => {
    previousFocusedElement.current = e.currentTarget;
    onCategoryClick?.();
  };

  const isFirstCategoryLevelShown =
    currentCategoryId === OFFCANVAS_DEFAULT_QUERY_PARAMETER;

  const previousFocusedElement = useFocusOnFirstLevelClickedButton(
    isFirstCategoryLevelShown,
  );

  const selectedSecondLevelCategory = treeItems.find(
    item =>
      item.item?.$case === 'treeItem' &&
      item.item.treeItem.category.id === Number(currentCategoryId),
  );

  return (
    <Wrapper>
      <FirstCategoryWrapper data-is-active={isFirstCategoryLevelShown}>
        <FirstLevelCategoryTree
          isTransitioning={isTransitioning}
          items={treeItems}
          onClick={onClick}
          onCategoryClick={onFirstLevelCategoryClick}
          makeInert={!isFirstCategoryLevelShown}
        />
      </FirstCategoryWrapper>
      <SecondCategoryWrapper data-is-active={!!selectedSecondLevelCategory}>
        {selectedSecondLevelCategory &&
          selectedSecondLevelCategory.item?.$case === 'treeItem' && (
            <SecondLevelCategoryTree
              category={selectedSecondLevelCategory.item.treeItem}
              isTransitioning={isTransitioning}
              onCategoryClick={onCategoryClick}
            />
          )}
      </SecondCategoryWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  overflow: hidden;
  position: relative;
`;

const ANIMATION_TIMING_FUNCTION = 'cubic-bezier(0.81, 0.08, 0.68, 0.53)';

const FirstCategoryWrapper = styled.div`
  transition: transform 0.15s ${ANIMATION_TIMING_FUNCTION},
    opacity 0.15s ${ANIMATION_TIMING_FUNCTION};
  transform: translateX(-20%);
  opacity: 0;
  position: absolute;
  top: 0;

  &[data-is-active='true'] {
    transform: translateX(0);
    opacity: 1;
    position: relative;
    width: 100%;
  }
`;

const SecondCategoryWrapper = styled.div`
  transition: transform 0.15s ${ANIMATION_TIMING_FUNCTION},
    opacity 0.15s ${ANIMATION_TIMING_FUNCTION};
  transform: translateX(20%);
  opacity: 0;
  top: 0;

  &[data-is-active='true'] {
    transform: translateX(0);
    opacity: 1;
    position: relative;
    width: 100%;
  }
`;
